// packages/shared/src/villagers/generate.ts — villager generation (spec §9.13)
import { COLOR_TAGS, FURNITURE_CATEGORIES, PERSONALITIES, SPECIES, villagerBank, type Personality, type VillagerBank, type VillagerSpecies } from '@whiskerton/content';
import { rngFrom } from '../rng';
import type { VillagerPublic } from '../types/villagers';

const VILLAGER_SPECIES = SPECIES.filter((s): s is VillagerSpecies => s !== 'person');
export const comboKey = (species: VillagerSpecies, personality: Personality) => `${species}:${personality}`;

/** Multiplies each RGB channel by (1 + amount); amount −0.3 = 30 % darker. */
export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => Math.max(0, Math.min(255, Math.round(((n >> shift) & 255) * (1 + amount))));
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

/** bank default: villagerBank (filled by M2-T02 in the same wave; M2-T03 tests pass a fixture bank). */
/** `taken` is what the town's villagers already have. Names and species+personality combos are always avoided; with `species` and
 *  `catchphrases` (M2 playtest: two cats among four founders, two villagers saying 'boop boop') a new villager also gets a species and a
 *  catchphrase nobody has yet, while the bank has any left. */
export function generateVillager(townSeed: number, n: number,
  taken: { names: ReadonlySet<string>; combos: ReadonlySet<string>; species?: ReadonlySet<string>; catchphrases?: ReadonlySet<string> },
  plot: number, now: number, bank: VillagerBank = villagerBank): Omit<VillagerPublic, 'id'> {
  const rng = rngFrom('vgen', townSeed, n);
  const freshSpecies = VILLAGER_SPECIES.filter((s) => !taken.species?.has(s));
  const pool = freshSpecies.length > 0 ? freshSpecies : VILLAGER_SPECIES;
  let species = rng.pick(pool);
  let personality = rng.pick(PERSONALITIES);
  for (let i = 0; i < 24 && taken.combos.has(comboKey(species, personality)); i++) {
    species = rng.pick(pool);
    personality = rng.pick(PERSONALITIES);
  }
  const spec = bank.species[species];
  const pal = rng.pick(spec.palettes);
  const pattern = rng.pick(spec.patterns);
  const names = rng.shuffle([...bank.names[species]]);
  const name = names.find((x) => !taken.names.has(x)) ?? names[n % names.length]!;
  const freshPhrases = bank.catchphrases.filter((c) => !taken.catchphrases?.has(c));
  return {
    species, personality, name, catchphrase: rng.pick(freshPhrases.length > 0 ? freshPhrases : bank.catchphrases),
    colors: { fur: pal.fur, ears: pal.ears, muzzle: pal.muzzle, pattern, patternColor: shade(pal.fur, -0.3), shirt: rng.pick(bank.shirtDyes) },
    plot, houseTheme: rng.pick(bank.houseThemes).id, movedInAt: now, protected: false,
    favCategory: rng.pick(FURNITURE_CATEGORIES), favColor: rng.pick(COLOR_TAGS),
  };
}
